import { useState } from 'react';
import type { CalculatorFormData, StepConfig } from '../calculatorTypes';
import { Stepper } from '../shared/Stepper';
import { getText } from '../calculatorUtils';
import type { CalcTexts } from '../locales';
import styles from '../Calculator.module.css';

interface Props {
  step: StepConfig;
  formData: CalculatorFormData;
  onNext: (data: Partial<CalculatorFormData>) => void;
  onBack: (data: Partial<CalculatorFormData>) => void;
  canGoBack: boolean;
  texts: CalcTexts;
}

export function StepperGroupsStep({ step, formData, onNext, onBack, canGoBack, texts }: Props) {
  const groups = step.groups!;

  const [values, setValues] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      groups.map(g => [g.key, (formData[g.key as keyof CalculatorFormData] as number) ?? 0])
    )
  );

  function buildData() {
    const total = Object.values(values).reduce((s, v) => s + v, 0);
    return { has_children: total > 0, ...values } as Partial<CalculatorFormData>;
  }

  function handleNext() {
    onNext(buildData());
  }

  const lastGroup = groups[groups.length - 1];
  const footnote = lastGroup?.hintKey ? getText(texts, lastGroup.hintKey) : undefined;

  return (
    <div className={styles.stepContent}>
      <p className={styles.stepQuestion}>{getText(texts, step.questionKey!)}</p>

      <div className={styles.stepperGroupList}>
        {groups.map((group, i) => {
          const label = getText(texts, group.labelKey);
          const isLast = i === groups.length - 1;
          return (
            <div key={group.key} className={styles.stepperGroupRow}>
              <Stepper
                value={values[group.key]}
                onChange={v => setValues(prev => ({ ...prev, [group.key]: v }))}
                label={label}
              />
              <span className={styles.stepperGroupLabel}>
                {label}{isLast ? '*' : ''}
              </span>
            </div>
          );
        })}
      </div>

      {footnote && <div className={styles.hintBox}>*{footnote}</div>}

      <div className={styles.stepNav}>
        <button
          type="button"
          className={styles.btnBack}
          onClick={() => onBack(buildData())}
          disabled={!canGoBack}
        >
          {'< '}{texts.common.buttons.back}
        </button>
        <button type="button" className={styles.btnSecondary} onClick={handleNext}>
          {texts.common.buttons.continue}
        </button>
      </div>
    </div>
  );
}
